import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardList, Search, User, FileText, Trash2, Briefcase,
  CheckCircle2, X, Edit2, ChevronLeft, ChevronRight,
  Users, MapPin, Clock, DollarSign,
  Calendar, MessageSquare, BadgeCheck, AlertCircle,
  MoreVertical, Eye, Filter, UserCheck, UserX
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostulacionesProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todas');
  const [selectedPost, setSelectedPost] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Modal Agendar state
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [schedulingData, setSchedulingData] = useState({
    fecha: '',
    hora: '10:00'
  });

  useEffect(() => {
    fetchData();
  }, [tenantSlug]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones`);
      setPostulaciones(response.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar postulaciones. Verifique el silo "postulaciones"');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (postId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones/${postId}`, { estado: newStatus });
      toast.success(`Candidato actualizado a ${newStatus}`);
      fetchData();
      if (selectedPost && selectedPost._id === postId) {
        setSelectedPost({...selectedPost, estado: newStatus});
      }
    } catch (err) {
      toast.error("Error al actualizar estado");
    }
  };

  const handleAgendarEntrevista = (postulacion) => {
    setSchedulingData({
      fecha: new Date().toISOString().split('T')[0],
      hora: '10:00'
    });
    setIsSchedulingOpen(true);
  };

  const confirmScheduling = async () => {
    if (!schedulingData.fecha || !schedulingData.hora) {
      toast.error("Por favor selecciona fecha y hora");
      return;
    }

    try {
      const combinedDateTime = `${schedulingData.fecha}T${schedulingData.hora}:00`;
      const citaPayload = {
        data: {
          folio: `ENT-${Math.floor(1000 + Math.random() * 9000)}`,
          cliente: `${selectedPost.nombre} (Postulación: ${selectedPost.vacante_ref})`,
          fecha_hora: combinedDateTime,
          servicio: "Entrevista de Reclutamiento",
          estado: "Programada",
          notas: `Vacante: ${selectedPost.vacante_ref}`,
          fecha_registro: new Date().toISOString()
        }
      };

      await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas/inject`, citaPayload);
      
      await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones/${selectedPost._id}`, { 
        estado: 'entrevista',
        cita_entrevista_ref: citaPayload.data.folio
      });

      toast.success("Entrevista agendada y registrada en Agenda");
      fetchData();
      setIsSchedulingOpen(false);
      setIsDetailOpen(false);
    } catch (err) {
      toast.error("Error al agendar. Verifique el silo 'citas'");
    }
  };

  const handleDelete = async (id) => {
    if(!window.confirm("¿Seguro que deseas eliminar esta postulación?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/postulaciones/${id}`);
      toast.success("Registro eliminado");
      fetchData();
      setIsDetailOpen(false);
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filtered = postulaciones.filter(p => {
    const matchesSearch = 
      p.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      p.vacante_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.telefono?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'todas' || p.estado === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: postulaciones.length,
    nuevas: postulaciones.filter(p => p.estado === 'nueva').length,
    revision: postulaciones.filter(p => p.estado === 'en_revision').length,
    entrevistas: postulaciones.filter(p => p.estado === 'entrevista').length
  };

  return (
    <div className="h-full flex flex-col pt-8 lg:pt-0">
      {/* Premium Blue Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-blue-400" />
                <span className="text-[9px] uppercase tracking-[0.4em] font-black text-blue-400">Talent Pipeline</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Postulaciones</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Seguimiento de candidatos y flujo de contratación.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-4 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[750px]"
            >
                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar candidato..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <div className="flex items-center gap-2 bg-slate-900/50 p-1 px-3 rounded-xl border border-white/5 overflow-hidden">
                  <div className="flex items-center gap-2 border-r border-white/5 pr-3 mr-1">
                    <Filter className="w-3 h-3 text-slate-500" />
                    <span className="text-[8px] font-black text-slate-600 uppercase tracking-[0.2em]">Filtros</span>
                  </div>
                  <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-0.5">
                    {['todas', 'nueva', 'en_revision', 'entrevista', 'contratado', 'rechazada'].map(status => (
                      <button
                        key={status}
                        onClick={() => setStatusFilter(status)}
                        className={`px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                          statusFilter === status 
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' 
                            : 'text-slate-500 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {status.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total', value: stats.total, icon: Users, color: 'slate' },
          { label: 'Nuevas', value: stats.nuevas, icon: Clock, color: 'blue' },
          { label: 'En Revisión', value: stats.revision, icon: Eye, color: 'indigo' },
          { label: 'Entrevistas', value: stats.entrevistas, icon: Calendar, color: 'amber' }
        ].map((kpi, i) => {
          const Icon = kpi.icon;
          return (
            <div key={i} className="bg-slate-900 border border-white/5 p-4 rounded-2xl flex items-center gap-4">
               <div className={`w-10 h-10 rounded-xl bg-${kpi.color}-500/10 flex items-center justify-center border border-${kpi.color}-500/20`}>
                  <Icon className={`w-5 h-5 text-${kpi.color}-400`} />
               </div>
               <div>
                  <p className="text-[10px] uppercase font-black text-slate-500 tracking-widest">{kpi.label}</p>
                  <p className="text-xl font-black text-white">{kpi.value}</p>
               </div>
            </div>
          );
        })}
      </div>

      {/* Candidates List */}
      <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Sincronizando pipeline...</div>
        ) : filtered.length === 0 ? (
          <div className="p-20 text-center bg-slate-900/50 border border-white/5 rounded-[2rem]">
             <ClipboardList className="w-12 h-12 text-slate-700 mx-auto mb-4" />
             <p className="text-lg text-slate-400 font-medium">No hay postulaciones que coincidan</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-10">
            {filtered.map((post, index) => (
              <motion.div 
                key={post._id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => { setSelectedPost(post); setIsDetailOpen(true); }}
                className="bg-slate-900 border border-white/5 rounded-2xl p-4 hover:border-blue-500/30 transition-all cursor-pointer group relative overflow-hidden flex flex-col justify-between"
              >
                <div>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-white border border-white/10 font-black text-lg shadow-inner flex-shrink-0">
                        {post.nombre?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-white tracking-tight leading-tight mb-0.5 truncate">{post.nombre}</h3>
                        <p className="text-[9px] text-slate-500 font-medium">{post.telefono}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Briefcase className="w-3 h-3 text-blue-400" />
                      <span className="text-[10px] text-white font-bold truncate">{post.vacante_ref || 'Sin vacante'}</span>
                    </div>
                    
                    <div className="bg-black/20 p-2 rounded-lg border border-white/5 min-h-[40px]">
                      <p className="text-[9px] text-slate-400 leading-tight italic line-clamp-2">
                        {post.experiencia || 'Sin detalle de experiencia...'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/5 mt-auto">
                  <span className={`px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase tracking-widest border ${
                    post.estado === 'nueva' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                    post.estado === 'entrevista' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                    post.estado === 'contratado' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                    'bg-slate-500/10 text-slate-400 border-slate-500/20'
                  }`}>
                    {post.estado}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-2.5 h-2.5 text-slate-600" />
                    <span className="text-[8px] text-slate-600 font-bold uppercase tracking-tighter">
                      {post.fecha_registro ? new Date(post.fecha_registro).toLocaleDateString() : 'Reciente'}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Sidebar */}
      <AnimatePresence>
        {isDetailOpen && selectedPost && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsDetailOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              className="fixed top-0 right-0 h-full w-full max-w-[550px] bg-[#0F172A] border-l border-white/10 z-[101] flex flex-col"
            >
              <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white font-black text-2xl shadow-lg">
                    {selectedPost.nombre?.charAt(0)}
                  </div>
                  <div>
                    <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{selectedPost.nombre}</h2>
                    <p className="text-blue-400 text-[10px] font-black uppercase tracking-widest">Detalle del Candidato</p>
                  </div>
                </div>
                <button onClick={() => setIsDetailOpen(false)} className="p-2 hover:bg-white/5 rounded-lg text-slate-500">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
                {/* Status Selection */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Estado de la Postulación</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['nueva', 'en_revision', 'entrevista', 'contratado', 'rechazada'].map(st => (
                      <button
                        key={st}
                        onClick={() => handleUpdateStatus(selectedPost._id, st)}
                        className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase transition-all border ${
                          selectedPost.estado === st 
                            ? 'bg-blue-600 border-blue-500 text-white shadow-lg' 
                            : 'bg-slate-900 border-white/5 text-slate-500 hover:border-white/20'
                        }`}
                      >
                        {st.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Candidate Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
                    <p className="text-[9px] font-black uppercase text-slate-500 mb-1">Teléfono</p>
                    <p className="text-sm text-white font-bold">{selectedPost.telefono}</p>
                  </div>
                  <div className="bg-white/[0.02] border border-white/5 p-4 rounded-2xl">
                    <p className="text-[9px] font-black uppercase text-slate-500 mb-1">Vacante Ref</p>
                    <p className="text-sm text-white font-bold">{selectedPost.vacante_ref}</p>
                  </div>
                </div>

                {/* Full Experience */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Perfil y Experiencia</label>
                  <div className="bg-slate-950/50 border border-white/5 p-6 rounded-[2rem] min-h-[150px]">
                    <p className="text-sm text-slate-300 leading-relaxed italic">
                      "{selectedPost.experiencia || 'El candidato no proporcionó información adicional durante la postulación por WhatsApp.'}"
                    </p>
                  </div>
                </div>

                {/* Actions Section */}
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Acciones Operativas</label>
                  <div className="flex flex-col gap-3">
                    <button 
                      onClick={() => handleAgendarEntrevista(selectedPost)}
                      className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-xl hover:-translate-y-1 transition-all flex items-center justify-center gap-3"
                    >
                      <Calendar className="w-5 h-5" />
                      Agendar Entrevista Presencial
                    </button>
                    
                    <button className="w-full py-4 bg-white/5 text-slate-300 font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/10 hover:bg-white/10 transition-all flex items-center justify-center gap-3">
                      <MessageSquare className="w-5 h-5" />
                      Contactar por WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-white/5 bg-white/[0.02]">
                <button 
                  onClick={() => handleDelete(selectedPost._id)}
                  className="w-full py-3 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl hover:bg-red-500 hover:text-white transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest"
                >
                  <Trash2 className="w-4 h-4" />
                  Eliminar Registro
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Scheduling Modal */}
      <AnimatePresence>
        {isSchedulingOpen && (
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/90 backdrop-blur-xl z-[120] flex items-center justify-center p-4"
              onClick={() => setIsSchedulingOpen(false)}
            >
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                className="w-full max-w-md bg-slate-900 border border-white/10 p-8 rounded-[2.5rem] shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
              <div className="flex flex-col items-center text-center mb-8">
                 <div className="w-16 h-16 rounded-3xl bg-blue-500/10 flex items-center justify-center border border-blue-500/20 mb-4">
                    <Calendar className="w-8 h-8 text-blue-400" />
                 </div>
                 <h2 className="text-2xl font-black text-white tracking-tighter uppercase italic">Programar Entrevista</h2>
                 <p className="text-slate-500 text-xs font-bold mt-1 uppercase tracking-widest">Selecciona disponibilidad</p>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-500 ml-1 tracking-widest">Fecha de la Cita</label>
                  <div className="relative group">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400" />
                    <input 
                      type="date"
                      className="w-full bg-slate-950 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white font-black text-sm outline-none focus:border-blue-500/50 transition-all"
                      value={schedulingData.fecha}
                      onChange={e => setSchedulingData({...schedulingData, fecha: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase text-slate-500 ml-1 tracking-widest">Hora de Inicio</label>
                  <div className="relative group">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 group-focus-within:text-blue-400" />
                    <input 
                      type="time"
                      className="w-full bg-slate-950 border border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white font-black text-sm outline-none focus:border-blue-500/50 transition-all"
                      value={schedulingData.hora}
                      onChange={e => setSchedulingData({...schedulingData, hora: e.target.value})}
                    />
                  </div>
                </div>

                <div className="pt-4 flex flex-col gap-3">
                  <button 
                    onClick={confirmScheduling}
                    className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl shadow-xl hover:-translate-y-1 transition-all"
                  >
                    Confirmar y Notificar
                  </button>
                  <button 
                    onClick={() => setIsSchedulingOpen(false)}
                    className="w-full py-4 bg-white/5 text-slate-500 font-black uppercase tracking-[0.2em] text-[10px] rounded-2xl border border-white/5 hover:bg-white/10 transition-all"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PostulacionesProPage;
